import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { DocumentTextIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import {
  deleteMeetingMinutesTemplate,
  fetchMeetingMinutesTemplates,
} from "../../../../api/meetingMinutes";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import {
  MeetingMinutesShell,
  mmCardClass,
  mmPrimaryButtonClass,
} from "../MeetingMinutesShell";
import { meetingTypeLabel } from "../MeetingMinutesTypePills";

export const MeetingMinutesTemplatesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetchMeetingMinutesTemplates({
        query: searchQuery,
        per_page: 50,
        page: 1,
      });
      setTemplates(response.data || []);
      setError(false);
    } catch (err) {
      setError(true);
      toast.error(t("meeting_minutes.template.load_error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [searchQuery]);

  const handleDelete = async (id) => {
    if (!window.confirm(t("meeting_minutes.template.delete_confirm"))) return;
    try {
      await deleteMeetingMinutesTemplate(id);
      toast.success(t("meeting_minutes.template.deleted"));
      fetchData();
    } catch (err) {
      toast.error(t("meeting_minutes.template.delete_error"));
    }
  };

  if (error) return <ResourceError />;

  return (
    <MeetingMinutesShell
      eyebrow={<><DocumentTextIcon className="h-4 w-4" />{t("meeting_minutes.templates.list")}</>}
      title={t("meeting_minutes.templates.list")}
      subtitle={t("meeting_minutes.templates.hero_subtitle")}
      backTo="/dashboard/meeting-minutes"
      backLabel={t("Back")}
      actions={
        <button
          type="button"
          onClick={() => navigate("/dashboard/meeting-minutes/templates/new")}
          className={mmPrimaryButtonClass()}
        >
          <PlusIcon className="h-4 w-4" />
          {t("meeting_minutes.templates.new")}
        </button>
      }
    >
      <div className={mmCardClass()}>
        <div className="border-b border-slate-100 px-6 py-5">
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("meeting_minutes.templates.search")}
            className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-[#1463C7] focus:outline-none focus:ring-2 focus:ring-[#1463C7]/20"
          />
        </div>
        {loading ? (
          <div className="px-6 py-10">
            <ResourceLoading />
          </div>
        ) : templates.length === 0 ? (
          <div className="px-6 py-12 text-sm text-slate-500">
            {t("meeting_minutes.templates.no_results")}
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {templates.map((template) => (
              <div key={template.id} className="flex items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50">
                <Link
                  to={`/dashboard/meeting-minutes/templates/${template.id}/edit`}
                  className="min-w-0 flex-1"
                >
                  <h3 className="truncate text-sm font-semibold text-slate-950">{template.title}</h3>
                  <p className="mt-1 text-xs text-slate-500">
                    {meetingTypeLabel(t, template.meeting_type)}
                    {template.groups?.length
                      ? ` · ${template.groups.length} ${t("meeting_minutes.points")}`
                      : ` · ${t("meeting_minutes.templates.no_points")}`}
                  </p>
                </Link>
                <button
                  type="button"
                  onClick={() => handleDelete(template.id)}
                  className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </MeetingMinutesShell>
  );
};
